/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { Modal, message, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { useMutation, queryCache } from 'react-query';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { appLog } from '~src/utils/log';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useProjectId } from '~src/hooks/useProjectId';
import {
  Label,
  RegistrationLabel,
  restoreGroupLabel,
} from '~src/services/api/labels';

export interface DeleteModalProps extends Partial<ModalProps> {
  onDone?: () => void;
  group: RegistrationLabel | null;
  label: Label | null;
}

export const RestoreLabelModal: React.FC<DeleteModalProps> = ({
  onCancel,
  onDone,
  group,
  label,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();

  const [doRestoreLabel, { isLoading }] = useMutation(restoreGroupLabel, {
    onSuccess: async (deletedLabel, {}) => {
      // hide modal
      // @ts-ignore
      onDone ? onDone() : onCancel && onCancel();
      queryCache.invalidateQueries('registrationLabelGroups');
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'delete-failed' }));
      appLog('RestoreLabelModal -> restoreLabel -> items', item);
      appLog('RestoreLabelModal -> restoreLabel -> err', error);
    },
  });

  const handleSubmit = async () => {
    if (group?.id && label?.id) {
      doRestoreLabel({
        projectId: projectId!,
        labelId: label?.id,
        groupId: group?.id,
        action: 'reactivate',
      });
    }
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'restore' })}
          icon={['fas', 'rotate-left']}
        />
      }
      okText={intl.formatMessage({ id: 'restore' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{ loading: isLoading }}
      onOk={handleSubmit}
      onCancel={onCancel}
      {...modalProps}>
      <Typography.Paragraph css={styles.text}>
        {intl.formatMessage(
          { id: 'restore-single-text' },
          {
            name: (label as Label)?.title,
          }
        )}
      </Typography.Paragraph>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  text: {
    textAlign: 'center',
    margin: 0,
    fontSize: 15,
  },
});
